import { useState, useMemo } from 'react'
import type { StateFinding } from '@/lib/apiTypes'
import { SegmentedFilter } from '@/shared/components/SegmentedFilter'
import { groupFindings, categoryCounts, filterGroups, CATEGORY_FILTERS, type CategoryFilter } from '@/features/state/lib/findingView'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { Tooltip } from '@/shared/components/Tooltip'
import { GroupedFindingRow } from '@/features/state/components/GroupedFindingRow'
import { CATEGORY_FILTER_COLOR, CATEGORY_FILTER_LABEL } from '@/features/state/lib/categoryFilters'

export function FindingsList({ findings, loading }: { findings: StateFinding[]; loading: boolean }) {
  const [filter, setFilter] = useState<CategoryFilter>('all')
  // A Set, not a single id: findings are for comparing, so one-open-at-a-time fights the task.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const groups = useMemo(() => groupFindings(findings), [findings])
  const counts = useMemo(() => categoryCounts(groups), [groups])
  const visible = useMemo(() => filterGroups(groups, filter), [groups, filter])

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (loading) {
    return <SkeletonTable headers={['', 'Severity', 'Category', 'Finding', 'Location', 'Detail', 'Count']} firstColTwoLines={false} rows={5} />
  }
  if (findings.length === 0) return <EmptyState icon="✓" title="Clean." />

  const showsSecret = visible.some((g) => g.view.bucket === 'secrets')

  return (
    <div className="fu">
      <p style={{ fontSize: '0.78rem', color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
        Secrets, misconfigurations, and exposure detected by scanning this state version. Repeats of
        one finding are grouped; expand a row to see every place it occurs.
      </p>
      <div style={{ marginBottom: 14 }}>
        <SegmentedFilter
          label="Filter findings by category"
          value={filter}
          onChange={setFilter}
          options={CATEGORY_FILTERS.map((k) => ({
            key: k,
            label: CATEGORY_FILTER_LABEL[k],
            count: counts[k],
            ...(CATEGORY_FILTER_COLOR[k] ? { color: CATEGORY_FILTER_COLOR[k]! } : {}),
          }))}
        />
      </div>

      {visible.length === 0 ? (
        // Distinct from the all-clear above: "no secrets" and "no findings" are different facts.
        <EmptyState icon="⬡" title="No findings match this filter." />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th><th><Tooltip text="Severity the scanner assigned to the finding." placement="top"><span tabIndex={0} className="cursor-help">Severity</span></Tooltip></th><th><Tooltip text="What kind of issue it is, e.g. a secret or a misconfiguration." placement="top"><span tabIndex={0} className="cursor-help">Category</span></Tooltip></th><th><Tooltip text="The rule or check that fired." placement="top"><span tabIndex={0} className="cursor-help">Finding</span></Tooltip></th><th><Tooltip text="Where in the state the finding was found." placement="top"><span tabIndex={0} className="cursor-help">Location</span></Tooltip></th><th><Tooltip text="Extra context from the scanner for this finding." placement="top"><span tabIndex={0} className="cursor-help">Detail</span></Tooltip></th><th><Tooltip text="How many occurrences are grouped into this row. Expand it for each one." placement="top"><span tabIndex={0} className="cursor-help">Count</span></Tooltip></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((g) => (
                <GroupedFindingRow key={g.key} group={g} expanded={expanded.has(g.key)} onToggle={() => toggle(g.key)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showsSecret && (
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)', marginTop: 8 }}>
          // Secret values are never stored. Rotate any affected credentials immediately.
        </div>
      )}
    </div>
  )
}

